// dealsTableSlice_Store.ts

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import type { DealsTableSliceState, DealDetail } from "./types/redux";
import { authenticatedFetch } from "./utils/authErrorHandler";

// --- Async thunk: fetch full deal details from API ---

export const fetchDealDetail = createAsyncThunk<
  { dealId: string; data: DealDetail },
  string,
  { state: { dealsTableSlice: DealsTableSliceState }; rejectValue: string }
>(
  "dealsTable/fetchDealDetail",
  async (dealId, { getState, rejectWithValue }) => {
    // Skip fetch if already cached
    const cached = getState().dealsTableSlice.dealDetailsCache[dealId];
    if (cached) {
      return { dealId, data: cached.data };
    }

    const baseUrl = import.meta.env.VITE_DEALS_API_BASE_URL;
    const response = await authenticatedFetch(
      `${baseUrl}/api/objects/deals/${dealId}`
    );

    if (!response.ok) {
      return rejectWithValue(
        `Failed to fetch deal details: ${response.status} ${response.statusText}`
      );
    }

    const data: DealDetail = await response.json();
    return { dealId, data };
  }
);

// --- Slice ---

const initialState: DealsTableSliceState = {
  activeDeals: [],
  selectedDeal: null,
  selectedDealTab: "summary",
  dealDetailsCache: {},
  dealDetailLoading: null,
  dealDetailError: null,
  isLoading: false,
  status: "idle",
  error: null,
};

const dealsTableSlice = createSlice({
  name: "dealsTable",
  initialState,
  reducers: {
    // Legacy reducers — used by ChatSlice dispatch sites
    fetchDealsPending: (state) => {
      state.status = "loading";
      state.activeDeals = [];
    },
    setActiveDeals: (
      state,
      action: PayloadAction<DealsTableSliceState["activeDeals"]>
    ) => {
      state.status = "succeeded";
      state.activeDeals = action.payload;
    },
    fetchDealsRejected: (state, action: PayloadAction<string>) => {
      state.status = "failed";
      state.error = action.payload;
    },
    clearActiveDeals: (state) => {
      state.activeDeals = [];
      state.status = "idle";
    },

    // New actions for deal detail panel
    openDealDetail: (state, action: PayloadAction<string>) => {
      state.selectedDeal = action.payload;
      state.selectedDealTab = "summary";
      state.dealDetailError = null;
    },
    closeDealDetail: (state) => {
      state.selectedDeal = null;
      state.dealDetailError = null;
    },
    setSelectedDealTab: (
      state,
      action: PayloadAction<"summary" | "docs">
    ) => {
      state.selectedDealTab = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDealDetail.pending, (state, action) => {
        state.dealDetailLoading = action.meta.arg;
        state.dealDetailError = null;
      })
      .addCase(fetchDealDetail.fulfilled, (state, action) => {
        const { dealId, data } = action.payload;
        state.dealDetailsCache[dealId] = {
          data,
          fetchedAt: Date.now(),
        };
        if (state.dealDetailLoading === dealId) {
          state.dealDetailLoading = null;
        }
      })
      .addCase(fetchDealDetail.rejected, (state, action) => {
        const dealId = action.meta.arg;
        if (state.dealDetailLoading === dealId) {
          state.dealDetailLoading = null;
          state.dealDetailError =
            action.payload ?? action.error.message ?? "Unknown error";
        }
      });
  },
});

export const {
  fetchDealsPending,
  setActiveDeals,
  fetchDealsRejected,
  clearActiveDeals,
  openDealDetail,
  closeDealDetail,
  setSelectedDealTab,
} = dealsTableSlice.actions;

// --- Selectors ---

export const selectSelectedDealId = (state: {
  dealsTableSlice: DealsTableSliceState;
}): string | null => state.dealsTableSlice.selectedDeal;

export const selectSelectedDealTab = (state: {
  dealsTableSlice: DealsTableSliceState;
}): "summary" | "docs" => state.dealsTableSlice.selectedDealTab;

export const selectDealDetail = (
  state: { dealsTableSlice: DealsTableSliceState },
  dealId: string | null
): DealDetail | undefined => {
  if (!dealId) return undefined;
  return state.dealsTableSlice.dealDetailsCache[dealId]?.data;
};

export const selectDealDetailLoading = (state: {
  dealsTableSlice: DealsTableSliceState;
}): string | null => state.dealsTableSlice.dealDetailLoading;

export const selectDealDetailError = (state: {
  dealsTableSlice: DealsTableSliceState;
}): string | null => state.dealsTableSlice.dealDetailError;

export default dealsTableSlice.reducer;
